import React, { useEffect, useMemo, useRef, useState } from 'react'
import { raDashboardService } from '../../services/report-dashboard/raDashboard.main.service'

const LOADING_MESSAGES = [
  'Submitting your report request…',
  'Request queued — waiting for the engine to pick it up…',
  'Engine is processing your portfolio data…',
  'Running risk calculations across holdings…',
  'Applying benchmark and covariance matrix settings…',
  'Computing factor exposures and contributions…',
  'Aggregating results and finalizing your report…',
  'Almost done — loading your dashboard data…',
]
const WAITING_MESSAGES = [
  'Still processing — the system may be under heavy load.',
  'Your report is still in the queue. Hang tight…',
  "This is taking longer than usual — we're still on it.",
  'Report generation can take several minutes for large portfolios.',
  'You can close this dialog and come back — the report will still run.',
]
const MSG_INTERVAL_SEC = 12
const WAITING_INTERVAL_SEC = 20
const POLL_INTERVAL_MS = 5_000
const PROGRESSIVE_DURATION_SEC = LOADING_MESSAGES.length * MSG_INTERVAL_SEC

// A report that's already resolved to a reportId (no polling needed) has uuid: null.
// A report still generating has a uuid to poll and reportId: null until it completes.
export interface ProgressPollTarget {
  uuid: string | null
  reportId: number | null
}

interface Props {
  targets: ProgressPollTarget[]
  active: boolean
  status: 'ready' | 'failed' | null
  isDarkMode: boolean
  onComplete: (reportIds: number[]) => void
  onCancel: () => void
  onFail: () => void
}

// A stable string derived from targets' identity, not the array reference — targets is
// rebuilt as a new array on every parent render, but polling should only (re)start when
// the actual set of things to poll changes.
const targetKeyOf = (targets: ProgressPollTarget[]): string =>
  targets.map((t) => t.uuid ?? `r:${t.reportId}`).join('|')

const ReportProgressManager: React.FC<Props> = ({
  targets,
  active,
  status,
  isDarkMode,
  onComplete,
  onCancel,
  onFail,
}) => {
  const [loadingElapsed, setLoadingElapsed] = useState(0)
  const [readyCount, setReadyCount] = useState(0)
  const pollingActiveRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const resolvedIdsRef = useRef<(number | null)[]>([])
  const resolvedCountRef = useRef(0)
  const completedRef = useRef(false)
  const failedRef = useRef(false)

  const onCompleteRef = useRef(onComplete)
  const onFailRef = useRef(onFail)
  onCompleteRef.current = onComplete
  onFailRef.current = onFail

  const targetKey = useMemo(() => targetKeyOf(targets), [targets])
  const pollableCount = useMemo(() => targets.filter((t) => t.uuid != null).length, [targets])

  useEffect(() => {
    return () => {
      pollingActiveRef.current = false
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (active && pollableCount > 0) {
      setLoadingElapsed(0)
      timerRef.current = setInterval(
        () => setLoadingElapsed((s) => s + 1),
        1000,
      )
    } else if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
    // pollableCount is derived from targetKey — see targetKey dep below for why targets itself isn't a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, targetKey])

  useEffect(() => {
    if (!active || targets.length === 0) return

    // Indexed by each target's original position, not resolution order — completion order is
    // nondeterministic (whichever polls happen to come back first), and callers rely on
    // reportIds[0] etc. lining up with the target they submitted at that position.
    resolvedIdsRef.current = new Array(targets.length).fill(null)
    resolvedCountRef.current = 0
    completedRef.current = false
    failedRef.current = false
    setReadyCount(0)
    pollingActiveRef.current = true

    const markResolved = (index: number, reportId: number) => {
      if (completedRef.current || resolvedIdsRef.current[index] != null) return
      resolvedIdsRef.current[index] = reportId
      resolvedCountRef.current += 1
      setReadyCount(resolvedCountRef.current)
      if (resolvedCountRef.current === targets.length) {
        completedRef.current = true
        onCompleteRef.current(resolvedIdsRef.current as number[])
      }
    }

    const poll = async (index: number, pollUuid: string) => {
      try {
        while (true) {
          await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
          if (!pollingActiveRef.current) return
          const statusResponse = await raDashboardService.getReportStatus(pollUuid)
          if (statusResponse.report_id > 0 && statusResponse.status === 'Completed') {
            markResolved(index, statusResponse.report_id)
            return
          }
        }
      } catch {
        if (!failedRef.current) {
          failedRef.current = true
          pollingActiveRef.current = false
          onFailRef.current()
        }
      }
    }

    targets.forEach((target, index) => {
      if (target.uuid === null && target.reportId !== null) {
        markResolved(index, target.reportId)
      } else if (target.uuid) {
        poll(index, target.uuid)
      }
    })
    // targets is intentionally omitted — targetKey is the stable proxy for "did the actual
    // set of things to poll change", so this only (re)starts polling when it should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, targetKey])

  const handleCancel = () => {
    pollingActiveRef.current = false
    onCancel()
  }

  const isBatch = targets.length > 1
  const showPopup = active && pollableCount > 0

  return (
    <>
      {/* Status badge — failure only; success state is shown by the summary block */}
      {status === 'failed' && !active && (
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#dc2626',
          }}
        >
          ✕ Failed to load data. Please try again later.
        </div>
      )}

      {/* Loading popup — only shown when polling for a new report */}
      {showPopup &&
        (() => {
          const inProgressivePhase = loadingElapsed < PROGRESSIVE_DURATION_SEC
          const message = isBatch
            ? `Generating attribution reports — ${readyCount} of ${targets.length} ready…`
            : inProgressivePhase
              ? LOADING_MESSAGES[Math.floor(loadingElapsed / MSG_INTERVAL_SEC)]
              : WAITING_MESSAGES[
                  Math.floor(
                    (loadingElapsed - PROGRESSIVE_DURATION_SEC) /
                      WAITING_INTERVAL_SEC,
                  ) % WAITING_MESSAGES.length
                ]
          const progress = isBatch
            ? (readyCount / targets.length) * 100
            : Math.min(92, (loadingElapsed / 60) * 100)

          return (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1100,
              }}
            >
              <style>{`
              @keyframes ra-fade-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
              @keyframes ra-shimmer { 0% { background-position:-400px 0 } 100% { background-position:400px 0 } }
            `}</style>

              <div
                style={{
                  position: 'relative',
                  width: '580px',
                  maxWidth: '90vw',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
                }}
              >
                {/* Progress bar */}
                <div
                  style={{
                    position: 'relative',
                    height: '5px',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(90deg, #0549dc, #6366f1, #0549dc)',
                      backgroundSize: '400px 100%',
                      animation: 'ra-shimmer 2s linear infinite',
                      width: `${progress}%`,
                      transition: 'width 1s linear',
                      borderRadius: '99px',
                    }}
                  />
                </div>

                {/* Card body */}
                <div
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(145deg, #1a1f2e, #141824)'
                      : 'linear-gradient(145deg, #ffffff, #f4f6fb)',
                    padding: '36px 40px 32px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  {/* Close button */}
                  <button
                    onClick={handleCancel}
                    style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '28px',
                      height: '28px',
                      background: isDarkMode
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.06)',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isDarkMode
                        ? 'rgba(255,255,255,0.5)'
                        : 'rgba(0,0,0,0.4)',
                      fontSize: '16px',
                      lineHeight: 1,
                      transition: 'background 0.2s, color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode
                        ? 'rgba(255,255,255,0.15)'
                        : 'rgba(0,0,0,0.12)'
                      e.currentTarget.style.color = isDarkMode ? '#fff' : '#000'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isDarkMode
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.06)'
                      e.currentTarget.style.color = isDarkMode
                        ? 'rgba(255,255,255,0.5)'
                        : 'rgba(0,0,0,0.4)'
                    }}
                  >
                    ✕
                  </button>

                  {/* Rotating message */}
                  <p
                    key={message}
                    style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '700',
                      lineHeight: '1.4',
                      background: isDarkMode
                        ? 'linear-gradient(135deg, #e2e8f0, #a5b4fc)'
                        : 'linear-gradient(135deg, #0f172a, #0549dc)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'ra-fade-in 0.5s ease',
                    }}
                  >
                    {message}
                  </p>

                  {/* Elapsed */}
                  <div
                    style={{
                      fontSize: '14px',
                      color: isDarkMode
                        ? 'rgba(255,255,255,0.35)'
                        : 'rgba(0,0,0,0.4)',
                    }}
                  >
                    {loadingElapsed}s elapsed
                  </div>

                  {/* Background run note */}
                  <div
                    style={{
                      fontSize: '14px',
                      color: isDarkMode
                        ? 'rgba(255,255,255,0.45)'
                        : 'rgba(0,0,0,0.5)',
                      borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                      paddingTop: '12px',
                    }}
                  >
                    You can close this dialog — the report will keep running in
                    the background. Come back in a few minutes and run the
                    report again to check if it's ready.
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
    </>
  )
}

export default ReportProgressManager
